// Pure acquisition-lattice placement (spec 2026-06-19, chord-choice). A node is a SET of
// acquired atoms (= a set of taken rungs); an edge adds one rung's bundle. Same-tier peers
// branch (≤ cap choices) and paths re-converge by set identity (a diamond). A tune lives on the
// shallowest state whose atoms cover its `uses`. To stay bounded we materialize only the
// canonical-prefix states of each tune's covering set (Phase A), then add every valid one-rung
// step between EXISTING states as a choice/diamond edge (Phase B) — so nodes scale with the
// corpus, not the power set. No engine/render imports.

const STEP_CAP = 3; // guardrail kept for skillStepViolations; acquisition deltas stay small.

export interface RungInput {
  atom: string; // the canonical atom this rung introduces (its "primary")
  drillId: string; // the drill that teaches it
  introduces: string[]; // every atom the drill grants (first chord also grants block + shape)
}
export interface ApplySong {
  id: string;
  uses: string[];
}
interface LatticeOpts {
  priorityOf: (atom: string) => readonly [number, number, string]; // [tier, freqRank, id]
  prereqsAtom: (atom: string) => string[];
  cap: number;
}

interface SkillNode {
  id: string; // "root" | "s:<sorted rung ids>"
  kind: "root" | "state";
  songIds: string[]; // covering tunes, else the representative drill
  uses: string[]; // acquired atoms (sorted)
  teaches: string[]; // [] — deltas live on edges (a state has many incoming deltas)
  isRoot?: boolean;
}
interface SkillEdge {
  from: string;
  to: string;
  delta: string[]; // atoms this edge introduces (sorted)
}
interface SkillGraph {
  nodes: SkillNode[];
  edges: SkillEdge[];
  overCap: string[]; // node ids whose retained out-degree exceeds cap (after repair) — logged
}
interface SkillStepViolation {
  id: string;
  delta: number;
  detail: string;
}

interface CandEdge {
  from: string;
  to: string;
  rung: RungInput;
  delta: string[];
}

const keyOf = (ids: readonly string[]): string => [...ids].sort().join("+");

const cmpPriority = (
  a: readonly [number, number, string],
  b: readonly [number, number, string],
): number => a[0] - b[0] || a[1] - b[1] || a[2].localeCompare(b[2]);

/** Build the acquisition lattice. */
export function buildAcquisitionLattice(
  rungs: readonly RungInput[],
  apply: readonly ApplySong[],
  opts: LatticeOpts,
): SkillGraph {
  const { priorityOf, prereqsAtom, cap } = opts;
  const rungById = new Map(rungs.map((r) => [r.drillId, r]));
  // Atoms anything can actually grant. A prereq outside it (e.g. `key-c`, taught only by an
  // external key drill, never a rung or a tune) is baseline — assumed satisfied, never blocks.
  const universe = new Set<string>([
    ...rungs.flatMap((r) => r.introduces),
    ...apply.flatMap((s) => s.uses),
  ]);
  const atomsOfRungs = (ids: Iterable<string>): Set<string> => {
    const out = new Set<string>();
    for (const id of ids) for (const a of rungById.get(id)?.introduces ?? []) out.add(a);
    return out;
  };
  // A rung's external prereqs: prereqs of any introduced atom the bundle doesn't itself grant.
  const prereqsOfRung = (r: RungInput): string[] => {
    const bundle = new Set(r.introduces);
    const out = new Set<string>();
    for (const a of r.introduces)
      for (const p of prereqsAtom(a)) if (!bundle.has(p) && universe.has(p)) out.add(p);
    return [...out];
  };
  // Canonical provider rung per atom. Primary first (the rung whose primary IS the atom — chords,
  // broken, alberti, …). Then bundled-only atoms (blocked-chords, triad-major, dom7) get the
  // lowest-priority rung that grants them, so a prereq like `triad-major` (needed by minor/dom7
  // chords) pulls in a major chord — you learn the major triad shape before the minor.
  const providerByAtom = new Map<string, RungInput>();
  for (const r of rungs) if (!providerByAtom.has(r.atom)) providerByAtom.set(r.atom, r);
  const byPriority = [...rungs].sort(
    (a, b) => cmpPriority(priorityOf(a.atom), priorityOf(b.atom)) || a.atom.localeCompare(b.atom),
  );
  for (const r of byPriority)
    for (const a of r.introduces) if (!providerByAtom.has(a)) providerByAtom.set(a, r);

  // Covering rung-set for a tune: the provider rungs of its used atoms, prereq-closed (pulls in
  // five-finger, a major chord for minor-triad prereqs, etc.).
  const coveringRungs = (uses: readonly string[]): string[] => {
    const set = new Set<string>();
    for (const a of uses) {
      const r = providerByAtom.get(a);
      if (r) set.add(r.drillId);
    }
    let changed = true;
    while (changed) {
      changed = false;
      for (const id of [...set]) {
        const r = rungById.get(id);
        if (!r) continue;
        for (const p of prereqsOfRung(r)) {
          const pr = providerByAtom.get(p);
          if (pr && !set.has(pr.drillId)) {
            set.add(pr.drillId);
            changed = true;
          }
        }
      }
    }
    return [...set];
  };

  // Target rung-sets (one per distinct tune uses), and the tunes that map to each.
  const targets = new Map<string, { rungIds: string[]; songIds: string[] }>();
  for (const song of apply) {
    const rungIds = coveringRungs(song.uses).sort();
    const k = keyOf(rungIds);
    const t = targets.get(k);
    if (t) t.songIds.push(song.id);
    else targets.set(k, { rungIds, songIds: [song.id] });
  }

  const root: SkillNode = {
    id: "root",
    kind: "root",
    songIds: [],
    uses: [],
    teaches: [],
    isRoot: true,
  };
  const stateKey = (ids: Set<string>): string => (ids.size === 0 ? "root" : `s:${keyOf([...ids])}`);
  const nodes = new Map<string, SkillNode>([["root", root]]);
  const stateIds = new Map<string, Set<string>>([["root", new Set()]]);
  const ensureNode = (ids: Set<string>): void => {
    const id = stateKey(ids);
    if (!nodes.has(id)) {
      nodes.set(id, {
        id,
        kind: "state",
        songIds: [],
        uses: [...atomsOfRungs(ids)].sort(),
        teaches: [],
      });
      stateIds.set(id, ids);
    }
  };

  // Phase A — bounded backbone: materialize only the canonical-prefix states of each target
  // (greedy topological order — add the highest-priority rung whose prereqs are met). Interning
  // by set key shares prefixes, so node count stays proportional to the corpus, not 2^atoms.
  for (const { rungIds } of targets.values()) {
    const remaining = new Set(rungIds);
    let cur = new Set<string>();
    while (remaining.size > 0) {
      const atoms = atomsOfRungs(cur);
      const addable = [...remaining]
        .map((id) => rungById.get(id))
        .filter((r): r is RungInput => !!r && prereqsOfRung(r).every((p) => atoms.has(p)));
      if (addable.length === 0) break; // unsatisfiable target (shouldn't happen)
      addable.sort(
        (a, b) =>
          cmpPriority(priorityOf(a.atom), priorityOf(b.atom)) || a.atom.localeCompare(b.atom),
      );
      const pick = addable[0];
      if (!pick) break;
      cur = new Set(cur).add(pick.drillId);
      remaining.delete(pick.drillId);
      ensureNode(cur);
    }
  }

  // Phase B — every valid one-rung step BETWEEN EXISTING states is a candidate edge. This adds
  // the peer "choice" diamonds (e.g. {C}->{C,F} and {C}->{C,G7} re-merging at {C,F,G7}) without
  // creating any new nodes.
  const candidates = new Map<string, CandEdge>();
  for (const [fromId, ids] of stateIds) {
    const atoms = atomsOfRungs(ids);
    for (const r of rungs) {
      if (ids.has(r.drillId)) continue;
      if (!prereqsOfRung(r).every((p) => atoms.has(p))) continue;
      const next = new Set(ids).add(r.drillId);
      const toId = stateKey(next);
      if (!nodes.has(toId)) continue; // connect only existing backbone states
      const delta = [...atomsOfRungs(next)].filter((a) => !atoms.has(a)).sort();
      candidates.set(`${fromId} ${toId}`, { from: fromId, to: toId, rung: r, delta });
    }
  }

  // Assign tunes / a representative drill to each node.
  for (const { rungIds, songIds } of targets.values()) {
    const node = nodes.get(stateKey(new Set(rungIds)));
    if (node) node.songIds.push(...songIds);
  }
  for (const node of nodes.values()) {
    if (node.isRoot) continue;
    node.songIds.sort();
    if (node.songIds.length === 0) {
      // waypoint: representative = the highest-priority rung's drill in this state
      const rep = [...(stateIds.get(node.id) ?? [])]
        .map((id) => rungById.get(id))
        .filter((r): r is RungInput => !!r)
        .sort((a, b) => cmpPriority(priorityOf(b.atom), priorityOf(a.atom)))[0];
      if (rep) node.songIds = [rep.drillId];
    }
  }

  // Choice cap (≤ cap) by child priority, then reachability repair (correctness > cap).
  const byFrom = new Map<string, CandEdge[]>();
  for (const c of candidates.values()) {
    const list = byFrom.get(c.from);
    if (list) list.push(c);
    else byFrom.set(c.from, [c]);
  }
  const kept = new Set<string>();
  const dropped: CandEdge[] = [];
  for (const [from, list] of byFrom) {
    list.sort(
      (a, b) =>
        cmpPriority(priorityOf(a.rung.atom), priorityOf(b.rung.atom)) || a.to.localeCompare(b.to),
    );
    list.forEach((c, i) => {
      if (i < cap) kept.add(`${from} ${c.to}`);
      else dropped.push(c);
    });
  }
  const reachable = (): Set<string> => {
    const seen = new Set<string>(["root"]);
    const stack = ["root"];
    while (stack.length) {
      const cur = stack.pop();
      if (cur === undefined) continue;
      for (const c of byFrom.get(cur) ?? [])
        if (kept.has(`${cur} ${c.to}`) && !seen.has(c.to)) {
          seen.add(c.to);
          stack.push(c.to);
        }
    }
    return seen;
  };
  dropped.sort(
    (a, b) =>
      cmpPriority(priorityOf(a.rung.atom), priorityOf(b.rung.atom)) || a.to.localeCompare(b.to),
  );
  for (let guard = 0; guard < dropped.length + 1; guard++) {
    const seen = reachable();
    if ([...nodes.keys()].every((id) => seen.has(id))) break;
    const fix = dropped.find(
      (c) => seen.has(c.from) && !seen.has(c.to) && !kept.has(`${c.from} ${c.to}`),
    );
    if (!fix) break;
    kept.add(`${fix.from} ${fix.to}`);
  }

  const edges: SkillEdge[] = [];
  const outDeg = new Map<string, number>();
  for (const c of candidates.values()) {
    if (!kept.has(`${c.from} ${c.to}`)) continue;
    edges.push({ from: c.from, to: c.to, delta: c.delta });
    outDeg.set(c.from, (outDeg.get(c.from) ?? 0) + 1);
  }
  edges.sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to));

  // A node's `teaches` = its smallest incoming-edge delta (the new skills along its easiest
  // path). This drives the curriculum-state unlock gate (`uses − teaches` = the prereqs from
  // the best predecessor), so a tune self-unlocks once those prereqs are met — preserving the
  // apply-preferred "learn by playing real music" behaviour. (Edges still carry the per-step
  // deltas for the map; this is the per-node summary.)
  const minDelta = new Map<string, string[]>();
  for (const e of edges) {
    const cur = minDelta.get(e.to);
    if (!cur || e.delta.length < cur.length) minDelta.set(e.to, e.delta);
  }
  for (const n of nodes.values()) if (!n.isRoot) n.teaches = minDelta.get(n.id) ?? [];

  const overCap = [...outDeg.entries()]
    .filter(([, d]) => d > cap)
    .map(([id]) => id)
    .sort();
  return { nodes: [...nodes.values()], edges, overCap };
}

/** Edges whose delta exceeds STEP_CAP. rep-tier edges exempt. Guardrail; should not fire. */
export function skillStepViolations(graph: { edges: readonly SkillEdge[] }): SkillStepViolation[] {
  return graph.edges
    .filter((e) => !e.to.startsWith("rep:") && !e.from.startsWith("rep:"))
    .filter((e) => e.delta.length > STEP_CAP)
    .map((e) => ({
      id: e.to,
      delta: e.delta.length,
      detail: `edge from ${e.from} adds ${e.delta.length} skills (> ${STEP_CAP}): ${e.delta.join(", ")}`,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
}
